import React from 'react';
import { Redirect } from 'expo-router';
import type { RedirectProps } from 'expo-router';
import { useShallow } from 'zustand/react/shallow';
import { ROUTES } from '@/shared/constants/routes';
import useStore from '@/stores/useStore';
import useNotificationStore from '@/stores/useNotificationStore';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import { useNylasOAuthReturn } from '@/shared/hooks/useNylasOAuthReturn';
import { IS_WEB } from '@/shared/constants/utility';

export default function MainRedirect() {
  const { isStylist } = useStore();
  const { isReturningFromOAuth, isOAuthSuccessful } = useNylasOAuthReturn();
  const { pendingWebRedirect, parsedDeepLink } = useNotificationStore(
    useShallow((state) => ({
      pendingWebRedirect: state.pendingWebRedirect,
      parsedDeepLink: state.parsedDeepLink,
    })),
  );

  if (isStylist === null) {
    return <FullScreenLoader />;
  }

  // Handle Nylas OAuth return first (highest priority)
  if (isReturningFromOAuth) {
    return (
      <Redirect
        href={
          {
            pathname: ROUTES.AVAILABILITY.path,
            params: { connected: String(isOAuthSuccessful) },
          } as RedirectProps['href']
        }
      />
    );
  }

  // Handle web redirect URLs (for post-auth redirection)
  if (IS_WEB && pendingWebRedirect) {
    __DEV__ &&
      console.log('Redirecting to stored web URL:', pendingWebRedirect);

    // Extract the path from the full URL for Expo Router
    try {
      const url = new URL(pendingWebRedirect);
      const redirectPath = url.pathname + url.search + url.hash;
      return <Redirect href={redirectPath as RedirectProps['href']} />;
    } catch (error) {
      __DEV__ && console.error('Error parsing web redirect URL:', error);
      // Fall through to default routing
    }
  }

  // Handle mobile deep links
  if (!IS_WEB && parsedDeepLink?.path) {
    __DEV__ &&
      console.log('Redirecting to deep link path:', parsedDeepLink.path);

    // Build the redirect href with query params if present
    const redirectHref = parsedDeepLink.queryParams
      ? ({
          pathname: parsedDeepLink.path,
          params: parsedDeepLink.queryParams,
        } as RedirectProps['href'])
      : (parsedDeepLink.path as RedirectProps['href']);

    if (isStylist && parsedDeepLink.path === ROUTES.EXPLORE.name) {
      return <Redirect href={ROUTES.SESSIONS.path} />;
    }

    return <Redirect href={redirectHref} />;
  }

  // Default routing based on user type
  return (
    <Redirect href={isStylist ? ROUTES.SESSIONS.path : ROUTES.EXPLORE.path} />
  );
}
